import { useCombobox } from 'downshift';
import { useRef, useState, Fragment } from 'react';

import { CloseOutlineIc, SearchOutlineIc } from '@dsch/dd-icons';

import {
  InputButton,
  InputButtonProps,
} from '../../../../components/Toolkit/InputButton/InputButton';
import { Text } from '../../../../components/ToolkitV2/Text/Text';

import {
  ListItem,
  InputWrapper,
  SearchIcon,
  KeywordSearchInput,
  ClearButton,
  Heading,
  List,
} from './KeywordSearch.styled';

import {
  SearchableDropdown,
  highlight,
} from '../../../../components/ToolkitV2/SearchableDropdown/SearchableDropdown';

import type { TextProps } from '../../../../components/ToolkitV2/Text/Text.typed';
import type { MakeModelOption } from '../../MakeModelKeywordSearch.typed';
import {
  DisplayContainer,
  DisplayContainerConfig,
} from '../../../../components/Support/DisplayContainer/DisplayContainer';
import { MakeModelKeywordSearchModal } from '../MakeModelKeywordSearchModal/MakeModelKeywordSearchModal';
import { theme } from '../../../../helpers/Theme';

type DownshiftKeywordSearchProps = {
  onSelect: (option: MakeModelOption) => void;
  initialItems: Array<MakeModelOption>;
  makeModelOptions?: Array<MakeModelOption>;
  modalDisplayConfig: DisplayContainerConfig;
  dropdownDisplayConfig: DisplayContainerConfig;
};

/**
 * This component is responsible for displaying the UI and managing its logic in an accessible manner.
 *
 * It contains a modal and dropdown. Each of these can be configured to be displayed or hidden on different screen sizes.
 * They both contain list items that display makes or make and model options.
 *
 * useCombobox from downshift is used to managed the internal state of the dropdown and list items for both and the modal and the dropdown.
 * As a result, the input of the component is consistent across screen sizes.
 *
 * The state of the modal is managed separately. React modal requires its own state for displaying the modal with the list and dismissing it.
 */
export const KeywordSearch = (props: DownshiftKeywordSearchProps) => {
  const {
    onSelect,
    initialItems,
    makeModelOptions,
    modalDisplayConfig,
    dropdownDisplayConfig,
  } = props;

  const toggleButtonRef = useRef<HTMLDivElement>(null);

  const [items, setItems] = useState<Array<MakeModelOption>>(initialItems);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const {
    isOpen,
    inputValue,
    setInputValue,
    getInputProps,
    getMenuProps,
    getToggleButtonProps,
    getItemProps,
    highlightedIndex,
    getLabelProps,
  } = useCombobox({
    items,
    itemToString: (item) => item?.label ?? '',
    // Triggered when user types in the input
    onInputValueChange: ({ inputValue }) => {
      if (!inputValue) {
        setItems(initialItems);
        return;
      }

      inputValue && makeModelOptions && setItems(makeModelOptions);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect(selectedItem);
      if (items !== initialItems && isModalOpen) closeModal();
    },
    stateReducer(_state, actionAndChanges) {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
          // Without this, the dropdown would close when an item from the initial list of makes is selected.
          toggleButtonRef.current?.focus();
          // Without this, the dropdown would close when the searchbox is selected.
          return {
            ...changes,
            isOpen: true,
          };
        // Without this, the dropdown would close when an item from the initial list of makes is selected.
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: items === initialItems,
          };
        // Without this, the focus is lost when an item is selected/dropdown is closed.
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          toggleButtonRef.current?.focus();
          return changes;
        default:
          return changes;
      }
    },
  });

  const TEXT_PROPS: TextProps = {
    size: 'M16',
    forwardedAs: 'span',
  };

  const KEYWORD_SEARCH_INPUT_PROPS = {
    type: 'search',
    placeholder: 'Start typing a make or model',
    'aria-label': 'Keyword search input',
  };

  const INPUT_BUTTON_PROPS: InputButtonProps = {
    placeholder: 'All makes/models',
    isActive: false,
    heightVariant: 'LARGE',
  };

  const ListItems = () => {
    return items.map((item, index) => {
      const { label, count, groupLabel } = item;
      return (
        <Fragment key={`${label}-${index}`}>
          {groupLabel && (
            <Heading weight="bold" color="GREY_DARKER" size="M16">
              {groupLabel}
            </Heading>
          )}
          <ListItem
            {...getItemProps({
              item,
              index,
            })}
            highlighted={highlightedIndex === index}
          >
            {
              <Text {...TEXT_PROPS} color="GREY_DARKER">
                {highlight({
                  item: label,
                  searchText: inputValue,
                })}
              </Text>
            }
            {count ? (
              <Text {...TEXT_PROPS} color="GREY_DARK">
                {' '}
                ({count})
              </Text>
            ) : (
              ''
            )}
          </ListItem>
        </Fragment>
      );
    });
  };

  return (
    <>
      {/* Modal */}
      <DisplayContainer config={modalDisplayConfig}>
        <InputButton
          onClick={openModal}
          value={inputValue}
          aria-label="Open make & model modal"
          {...INPUT_BUTTON_PROPS}
        />
        <MakeModelKeywordSearchModal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
        >
          <label {...getLabelProps()}>
            <InputWrapper
              margin={`${theme.donedeal.spacing.S8} ${theme.donedeal.spacing.M16} 0`}
            >
              <SearchIcon aria-label="Search">
                <SearchOutlineIc />
              </SearchIcon>
              <KeywordSearchInput
                {...KEYWORD_SEARCH_INPUT_PROPS}
                {...getInputProps()}
              />
              {inputValue && (
                <ClearButton
                  onClick={() => {
                    setInputValue('');
                  }}
                  aria-label="Clear search"
                  ofType="GHOST"
                >
                  <CloseOutlineIc width={16} height={16} />
                </ClearButton>
              )}
            </InputWrapper>
          </label>
          {/**
           * This ensures that only one set of items is rendered, avoiding unexpected behaviour from sharing inputItemProps.
           *
           * These list items should always be displayed when the modal is opened.
           **/}
          <List {...getMenuProps()}>{isModalOpen && <ListItems />}</List>
        </MakeModelKeywordSearchModal>
      </DisplayContainer>

      {/* Dropdown */}
      <DisplayContainer config={dropdownDisplayConfig}>
        {!isOpen && (
          <InputButton
            value={inputValue}
            aria-label="Toggle make & model dropdown"
            {...INPUT_BUTTON_PROPS}
            {...getToggleButtonProps({ tabIndex: 0 })}
          />
        )}
        <SearchableDropdown isOpen={isOpen}>
          <label {...getLabelProps()}>
            <InputWrapper>
              <SearchIcon aria-label="Search">
                <SearchOutlineIc />
              </SearchIcon>
              <KeywordSearchInput
                {...KEYWORD_SEARCH_INPUT_PROPS}
                {...getInputProps()}
              />
              {inputValue && (
                <ClearButton
                  onClick={() => {
                    setInputValue('');
                  }}
                  aria-label="Clear search"
                  ofType="GHOST"
                >
                  <CloseOutlineIc width={16} height={16} />
                </ClearButton>
              )}
            </InputWrapper>
          </label>
          <List
            {...getMenuProps({ height: '337px' })}
            borderRadius={`0 0 ${theme.donedeal.borderRadius.default} ${theme.donedeal.borderRadius.default}`}
          >
            {/**
             * This ensures that only one set of items is rendered, avoiding unexpected behaviour from sharing inputItemProps.
             *
             * isOpen may be true for both sets of list items, when the user interacts with the list items in the modal, and when the toggle button
             * is clicked to open the dropdown.
             *
             * As such, there is an extra condition to determine that the modal is not open and therefore its list is not rendered
             * before rendering this list.
             **/}
            {!isModalOpen && isOpen && <ListItems />}
          </List>
        </SearchableDropdown>
      </DisplayContainer>
    </>
  );
};
